import {getTestProps} from '@benchling/js-test-utils';
import {StyleSheet, css} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import {isLLMFeedbackEnabled} from 'llm/feedback/LLMFeedbackUtils';
import {
  CheckEntryDisplayResult,
  CheckEntryResultUsefulState,
} from 'notebook/ai-tools/NotebookAIToolsConstants';
import Checkbox from 'ui-library/Checkbox';
import {GRAY, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';

interface AICheckerResultProps {
  result: CheckEntryDisplayResult;
  onToggleCompleted: () => void;
  onToggleUseful: () => void;
  onToggleNotUseful: () => void;
  suppressCompletedStrikethrough?: boolean;
}

/**
 * Individual result from the AI checker, which can be marked by the user as done or as not useful.
 */
export default function AICheckerResult({
  result,
  onToggleCompleted,
  onToggleUseful,
  onToggleNotUseful,
  suppressCompletedStrikethrough = false,
}: AICheckerResultProps): JSX.Element {
  const isUseful = result.usefulState === CheckEntryResultUsefulState.USEFUL;
  const isNotUseful = result.usefulState === CheckEntryResultUsefulState.NOT_USEFUL;
  const isMarked = result.usefulState !== CheckEntryResultUsefulState.UNMARKED;
  return (
    <div
      className={css(styles.checkResult)}
      {...getTestProps({component: 'AICheckerResult', element: 'root'})}
    >
      <div className={css(styles.checkbox, isNotUseful && styles.checkResultNotUseful)}>
        <Checkbox checked={result.isCompleted} onChange={onToggleCompleted} />
      </div>
      <div>
        <span
          className={css(
            isNotUseful && styles.checkResultNotUseful,
            result.isCompleted && !suppressCompletedStrikethrough && styles.checkResultCompleted
          )}
        >
          {result.text}
        </span>
        {isLLMFeedbackEnabled() && (
          <>
            <a
              className={classNames(
                'fa fa-thumbs-up',
                css(
                  styles.thumbsLink,
                  isNotUseful && styles.thumbsLinkInactive,
                  isMarked && styles.thumbsLinkVisible
                )
              )}
              aria-label="Mark useful"
              {...getActionButtonRoleProps({onPress: onToggleUseful})}
            />
            <a
              className={classNames(
                'fa fa-thumbs-down',
                css(
                  styles.thumbsLink,
                  isUseful && styles.thumbsLinkInactive,
                  isMarked && styles.thumbsLinkVisible
                )
              )}
              aria-label="Mark not useful"
              {...getActionButtonRoleProps({onPress: onToggleNotUseful})}
            />
            {isMarked && (
              <span className={css(styles.usefulLabel)}>{isUseful ? 'Useful' : 'Not useful'}</span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  checkResult: {
    alignItems: 'center',
    display: 'flex',
    lineHeight: 1.3,
    paddingTop: 5,
    paddingBottom: 5,
    ':hover': {
      backgroundColor: GRAY_LIGHTER,
    },
    ':hover .fa-thumbs-up': {
      visibility: 'visible',
    },
    ':hover .fa-thumbs-down': {
      visibility: 'visible',
    },
  },
  checkbox: {
    display: 'flex',
    marginRight: 10,
  },
  checkResultCompleted: {
    textDecoration: 'line-through',
  },
  checkResultNotUseful: {
    opacity: 0.4,
  },
  thumbsLink: {
    display: 'inline',
    fontSize: 14,
    marginLeft: 10,
    visibility: 'hidden',
    ':hover': {
      textDecoration: 'none',
    },
  },
  thumbsLinkInactive: {
    color: GRAY,
  },
  thumbsLinkVisible: {
    visibility: 'visible',
  },
  usefulLabel: {
    backgroundColor: GRAY_LIGHT,
    borderRadius: 10,
    fontSize: 11,
    marginLeft: 10,
    padding: '2px 5px',
    whiteSpace: 'nowrap',
  },
});
